import type { Ref } from "react";

import { useTranslations } from "@ui/providers/useTranslations";

import type { PasswordProps as CorePasswordProps } from "@jti/core-components/components/Password";
import { Password as CorePassword } from "@jti/core-components/components/Password";

import { usePasswordRequirements } from "./hooks";

export type PasswordProps = CorePasswordProps & {
  id?: string;
  value?: string;
  onChange?: CorePasswordProps["onChange"];
  disabled?: boolean;
  label?: string;
  dataTestId?: string;
  email?: string;
  withValidationRules?: boolean;
  isError?: boolean;
  isSuccess?: boolean;
  isOptionalLabelVisible?: boolean;
  showRequiredAsterisk?: boolean;
  errorText?: string;
  successText?: string;
  ref?: Ref<HTMLInputElement>;
};

export const Password = ({
  withValidationRules,
  email,
  dataTestId = "textInput",
  label,
  value,
  disabled,
  isError,
  isSuccess,
  isOptionalLabelVisible,
  showRequiredAsterisk,
  errorText,
  successText,
  onChange,
  id,
  ref,
  ...rest
}: PasswordProps) => {
  const { __ } = useTranslations();

  const passwordValue = `${value ?? ""}`;

  const { requirements, isPending } = usePasswordRequirements({
    passwordValue,
    email,
    enabled: Boolean(withValidationRules),
  });

  const getStatus = (): CorePasswordProps["status"] => {
    if (isError) return "error";
    if (isSuccess) return "success";
    return "default";
  };

  const getSupportingText = () => {
    if (isSuccess && successText) return successText;
    if (isError && errorText) return errorText;
    return undefined;
  };

  const getLabelFields = (): CorePasswordProps["labelFields"] => {
    if (!label) return undefined;
    if (isOptionalLabelVisible && !showRequiredAsterisk) {
      return { label, necessityIndicator: true, optionalText: __("form_fields.optional") };
    }
    return { label, asterisk: true };
  };

  return (
    <CorePassword
      {...rest}
      ref={ref}
      value={passwordValue}
      onChange={onChange}
      labelFields={getLabelFields()}
      status={getStatus()}
      helperText={getSupportingText()}
      disabled={disabled || isPending}
      showPasswordLabel={__("password.show")}
      hidePasswordLabel={__("password.hide")}
      requirements={requirements}
      pending={isPending}
      id={id}
      data-testid={dataTestId}
    />
  );
};
